import { useState } from 'react';
import type { LogEntry } from '../lib/types';

export default function Logs() {
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock logs data
  const logs: LogEntry[] = [
    {
      id: '1',
      projectId: '1',
      serverId: 'server-01',
      timestamp: new Date(Date.now() - 60000),
      level: 'error',
      message: 'Database connection timeout after 30s',
      context: { connectionString: 'postgres://...', retryAttempt: 3 }
    },
    {
      id: '2',
      projectId: '1',
      componentId: 'component-05',
      timestamp: new Date(Date.now() - 120000),
      level: 'warn',
      message: 'High memory usage detected: 85%',
      context: { currentMemory: '3.4GB', threshold: '4GB' }
    },
    {
      id: '3',
      projectId: '1',
      serverId: 'server-02',
      timestamp: new Date(Date.now() - 180000),
      level: 'info',
      message: 'Health check passed successfully',
      context: { endpoint: '/health', responseTime: '45ms' }
    },
    {
      id: '4',
      projectId: '2',
      serverId: 'server-03',
      timestamp: new Date(Date.now() - 240000),
      level: 'debug',
      message: 'Cache invalidation completed',
      context: { cacheKeys: 15, duration: '120ms' }
    }
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'fatal': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'error': return 'bg-red-100 text-red-700 border-red-200';
      case 'warn': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'debug': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'fatal':
      case 'error': return 'ri-error-warning-line';
      case 'warn': return 'ri-alert-line';
      case 'info': return 'ri-information-line';
      case 'debug': return 'ri-bug-line';
      default: return 'ri-file-list-line';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesLevel = selectedLevel === 'all' || log.level === selectedLevel;
    const matchesSearch = searchQuery === '' ||
      log.message.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesLevel && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Logs</h1>
            <p className="text-gray-600">Browse and search system logs</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>

            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Levels</option>
              <option value="fatal">Fatal</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>

            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <i className="ri-download-line mr-2"></i>
              Export
            </button>
          </div>
        </div>

        {/* Log Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          {[
            { label: 'Fatal', count: logs.filter(l => l.level === 'fatal').length, color: 'purple' },
            { label: 'Error', count: logs.filter(l => l.level === 'error').length, color: 'red' },
            { label: 'Warning', count: logs.filter(l => l.level === 'warn').length, color: 'yellow' },
            { label: 'Info', count: logs.filter(l => l.level === 'info').length, color: 'blue' },
            { label: 'Debug', count: logs.filter(l => l.level === 'debug').length, color: 'gray' }
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-600 mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold text-${stat.color}-600`}>{stat.count}</div>
            </div>
          ))}
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Log Entries ({filteredLogs.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredLogs.map((log, index) => (
              <div key={log.id} className={`p-4 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-32 text-sm text-gray-500 font-mono">
                    {log.timestamp.toLocaleTimeString()}
                  </div>

                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getLevelColor(log.level)}`}>
                      <i className={`${getLevelIcon(log.level)} mr-1`}></i>
                      {log.level.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-1">{log.message}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {log.serverId && (
                        <span className="flex items-center">
                          <i className="ri-server-line mr-1"></i>
                          {log.serverId}
                        </span>
                      )}
                      {log.componentId && (
                        <span className="flex items-center">
                          <i className="ri-stack-line mr-1"></i>
                          {log.componentId}
                        </span>
                      )}
                    </div>
                    {log.context && Object.keys(log.context).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                          View context
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <i className="ri-more-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredLogs.length === 0 && (
              <div className="p-12 text-center">
                <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No logs found</h3>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
